import React, { useState, useRef } from 'react';
import { analyzeLogScenario, AnalysisResult } from '../services/gemini';
import { 
  Upload, 
  FileArchive, 
  Play, 
  Loader2, 
  AlertTriangle, 
  FileCode, 
  GitBranch, 
  ChevronRight,
  TerminalSquare,
  Cpu
} from 'lucide-react';

export const AgentDashboard: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [problemDesc, setProblemDesc] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Simulate console logs streaming during "processing"
  const addLog = (msg: string) => {
    setLogs(prev => [...prev, `> ${msg}`]);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleAnalyze = async () => {
    if (!problemDesc) return;
    
    setIsAnalyzing(true);
    setResult(null);
    setLogs([]);
    
    // Simulate the steps of the architecture
    addLog("正在初始化 AI 智能体...");
    await new Promise(r => setTimeout(r, 800));
    
    addLog(file ? `正在解压归档文件: ${file.name}...` : "未提供归档，运行在模拟模式...");
    await new Promise(r => setTimeout(r, 1000));
    
    addLog("正在建立文件系统树索引...");
    addLog("根据查询识别相关模块: " + problemDesc.substring(0, 20) + "...");
    await new Promise(r => setTimeout(r, 800));

    addLog("正在分析内核环形缓冲区 (dmesg)...");
    addLog("正在扫描 var/log/syslog...");
    
    try {
      const data = await analyzeLogScenario(problemDesc);
      addLog("正在将错误与 Git 仓库关联...");
      await new Promise(r => setTimeout(r, 500));
      addLog("正在生成最终报告...");
      setResult(data);
    } catch (e) {
      addLog("分析过程中出错。");
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="flex h-full gap-6 p-6 w-full">
      {/* Left Column: Input */}
      <div className="w-1/3 flex flex-col gap-6">
        
        {/* Upload Section */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl">
          <h3 className="text-lg font-semibold mb-4 text-slate-200 flex items-center gap-2">
            <Upload className="w-5 h-5 text-cyan-500" />
            输入数据
          </h3>
          
          <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 hover:bg-slate-800/50 transition-colors text-center cursor-pointer relative">
             <input 
              type="file" 
              accept=".zip,.tar,.gz,.rar,.7z"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            {file ? (
              <div className="flex flex-col items-center text-cyan-400">
                <FileArchive className="w-10 h-10 mb-2" />
                <span className="font-medium">{file.name}</span>
                <span className="text-xs text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
              </div>
            ) : (
              <div className="flex flex-col items-center text-slate-500">
                <FileArchive className="w-10 h-10 mb-2" />
                <span className="text-sm">支持 .tar, .gz, .rar, .zip</span>
                <span className="text-xs text-slate-600 mt-1">点击或拖拽上传</span>
              </div>
            )}
          </div>
        </div>

        {/* Problem Description */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl flex-1 flex flex-col">
          <h3 className="text-lg font-semibold mb-4 text-slate-200">问题描述</h3>
          <textarea
            className="w-full flex-1 bg-slate-950 border border-slate-700 rounded-lg p-4 text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none font-mono text-sm"
            placeholder="描述问题，例如：'Wi-Fi 连接时系统随机重启' 或 '休眠唤醒后触摸屏驱动失效'..."
            value={problemDesc}
            onChange={(e) => setProblemDesc(e.target.value)}
          />
          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing || !problemDesc}
            className={`mt-4 w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-all ${
              isAnalyzing || !problemDesc
                ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/25'
            }`}
          >
            {isAnalyzing ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> 分析日志中...</>
            ) : (
              <><Play className="w-5 h-5" /> 开始 AI 分析</>
            )}
          </button>
        </div>
      </div>

      {/* Right Column: Output */}
      <div className="w-2/3 flex flex-col gap-6">
        
        {/* Terminal/Process View */}
        <div className="bg-black border border-slate-800 rounded-xl p-4 h-48 overflow-hidden flex flex-col font-mono text-xs shadow-inner">
          <div className="flex items-center gap-2 border-b border-slate-800 pb-2 mb-2 text-slate-500">
            <TerminalSquare className="w-4 h-4" />
            <span>智能体处理日志</span>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-1 text-green-500/80 p-2">
            {logs.length === 0 && <span className="text-slate-700 italic">等待输入...</span>}
            {logs.map((log, i) => (
              <div key={i}>{log}</div>
            ))}
            {isAnalyzing && <div className="animate-pulse">_</div>}
          </div>
        </div>

        {/* Results Area */}
        <div className="flex-1 bg-slate-900 border border-slate-800 rounded-xl p-8 overflow-y-auto shadow-xl relative">
          {!result ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-600 opacity-50">
              <Cpu className="w-24 h-24 mb-4 stroke-1" />
              <p className="text-lg">分析结果将显示在这里</p>
            </div>
          ) : (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
              
              {/* Header */}
              <div className="border-b border-slate-800 pb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 text-red-400 text-xs font-medium mb-3 border border-red-500/20">
                  <AlertTriangle className="w-3 h-3" />
                  检测到关键问题
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">{result.title}</h2>
                <p className="text-slate-400">{result.summary}</p>
              </div>

              {/* Grid for Modules and Files */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-950/50 p-4 rounded-lg border border-slate-800">
                  <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-cyan-500" /> 受影响模块
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {result.relevantModules.map(mod => (
                      <span key={mod} className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-300 font-mono border border-slate-700">
                        {mod}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-950/50 p-4 rounded-lg border border-slate-800">
                  <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-purple-500" /> 源代码文件
                  </h4>
                  <div className="space-y-1">
                    {result.suggestedGitFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs text-slate-400 hover:text-cyan-400 cursor-pointer transition-colors group">
                        <FileCode className="w-3 h-3 group-hover:text-cyan-400" />
                        {file}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Logs Evidence */}
              <div>
                <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-red-500" /> 错误证据
                </h4>
                <div className="bg-black/50 border border-red-900/30 rounded-lg p-4 font-mono text-xs overflow-x-auto">
                  {result.detectedErrors.map((err, i) => (
                    <div key={i} className="text-red-400/90 mb-1 border-l-2 border-red-500/50 pl-2 py-1 bg-red-950/10">
                      {err}
                    </div>
                  ))}
                </div>
              </div>

              {/* Root Cause */}
              <div className="bg-blue-950/10 border border-blue-900/30 p-6 rounded-lg">
                <h4 className="text-lg font-semibold text-blue-100 mb-3">根因分析</h4>
                <p className="text-slate-300 leading-relaxed text-sm">
                  {result.rootCauseAnalysis}
                </p>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};